
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useLanguage } from '@/contexts/LanguageContext';
import { Card, CardContent } from '@/components/ui/card';
import { supabase } from '@/integrations/supabase/client';
import { Building, Shield, Users, TrendingUp, Calendar, MessageCircle, Clock, Star } from 'lucide-react';

interface KPICardsProps {
  period: string;
}

const KPICards: React.FC<KPICardsProps> = ({ period }) => {
  const { t } = useLanguage();

  const { data: kpis, isLoading } = useQuery({
    queryKey: ['admin-kpis', period],
    queryFn: async () => {
      const now = new Date();
      const periodDays = parseInt(period);
      const startDate = new Date(now.getTime() - periodDays * 24 * 60 * 60 * 1000);

      // Active Listings
      const { count: activeListings } = await supabase
        .from('listings')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'active');

      // Pending Listing Verifications
      const { count: pendingVerifications } = await supabase
        .from('listings')
        .select('*', { count: 'exact', head: true })
        .eq('verified', false)
        .eq('status', 'active');

      // Verified Owners
      const { count: verifiedOwners } = await supabase
        .from('profiles')
        .select('*', { count: 'exact', head: true })
        .eq('role', 'landlord')
        .eq('kyc_status', 'VERIFIED');

      // Active Students (last 30 days)
      const { count: activeStudents } = await supabase
        .from('profiles')
        .select('*', { count: 'exact', head: true })
        .eq('role', 'student')
        .gte('last_seen', startDate.toISOString());

      // New Signups (7 days for this metric)
      const signupStartDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      const { count: newSignups } = await supabase
        .from('profiles')
        .select('*', { count: 'exact', head: true })
        .gte('created_at', signupStartDate.toISOString());

      // Bookings (30 days)
      const bookingStartDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
      const { count: bookings } = await supabase
        .from('bookings')
        .select('*', { count: 'exact', head: true })
        .gte('created_at', bookingStartDate.toISOString());

      // Visit to Booking Conversion
      const { count: visits } = await supabase
        .from('visits')
        .select('*', { count: 'exact', head: true })
        .gte('created_at', startDate.toISOString());

      const conversionRate = visits && visits > 0 ? ((bookings || 0) / visits * 100) : 0;

      return {
        activeListings: activeListings || 0,
        pendingVerifications: pendingVerifications || 0,
        verifiedOwners: verifiedOwners || 0,
        activeStudents: activeStudents || 0,
        newSignups: newSignups || 0,
        bookings: bookings || 0,
        conversionRate: Math.round(conversionRate),
        avgResponseTime: 2.4 // Mock data - would need chat analysis
      };
    }
  });

  const cards = [
    {
      title: t('active_listings', 'القوائم النشطة', 'Active Listings'),
      value: kpis?.activeListings,
      icon: Building,
      color: 'text-blue-600'
    },
    {
      title: t('pending_verifications', 'التحققات المعلقة', 'Pending Verifications'),
      value: kpis?.pendingVerifications,
      icon: Shield,
      color: 'text-yellow-600'
    },
    {
      title: t('verified_owners', 'المالكون المتحققون', 'Verified Owners'),
      value: kpis?.verifiedOwners,
      icon: Users,
      color: 'text-green-600'
    },
    {
      title: t('active_students', 'الطلاب النشطون', 'Active Students'),
      value: kpis?.activeStudents,
      icon: Users,
      color: 'text-purple-600'
    },
    {
      title: t('new_signups_7d', 'التسجيلات الجديدة (7د)', 'New Signups (7d)'),
      value: kpis?.newSignups,
      icon: TrendingUp,
      color: 'text-indigo-600'
    },
    {
      title: t('bookings_30d', 'الحجوزات (30د)', 'Bookings (30d)'),
      value: kpis?.bookings,
      icon: Calendar,
      color: 'text-orange-600'
    },
    {
      title: t('conversion_rate', 'معدل التحويل %', 'Conversion Rate %'),
      value: `${kpis?.conversionRate}%`,
      icon: TrendingUp,
      color: 'text-red-600'
    },
    {
      title: t('avg_response_time', 'متوسط وقت الرد (ساعات)', 'Avg Response Time (hrs)'),
      value: kpis?.avgResponseTime,
      icon: Clock,
      color: 'text-teal-600'
    }
  ];

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {Array.from({ length: 8 }).map((_, i) => (
          <Card key={i}>
            <CardContent className="p-6">
              <div className="animate-pulse">
                <div className="h-4 bg-muted rounded w-3/4 mb-2"></div>
                <div className="h-8 bg-muted rounded w-1/2"></div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
      {cards.map((card, index) => (
        <Card key={index}>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">{card.title}</p>
                <p className="text-2xl font-bold">{card.value}</p>
              </div>
              <card.icon className={`h-8 w-8 ${card.color}`} />
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

export default KPICards;
